import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { BarChart3, Play, Save, Trash2, Download, Filter, Columns, Table2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { formatCurrency } from '../services/api';

export default function CustomReportBuilder() {
  const [sources, setSources] = useState({});
  const [selectedSource, setSelectedSource] = useState('');
  const [sourceFields, setSourceFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filters, setFilters] = useState({});
  const [groupBy, setGroupBy] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [reportName, setReportName] = useState('');
  const [filterKey, setFilterKey] = useState('');
  const [filterVal, setFilterVal] = useState('');

  useEffect(() => {
    api.get('/reports/sources').then(r => setSources(r.data?.sources || {})).catch(() => {});
    api.get('/reports/saved').then(r => setSavedReports(r.data?.reports || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedSource && sources[selectedSource]) {
      const f = sources[selectedSource].fields;
      setSourceFields(f);
      setSelectedFields([...f]);
      setSortBy(sources[selectedSource].date_field || f[0]);
    }
  }, [selectedSource, sources]);

  const toggleField = (field) => {
    setSelectedFields(prev => prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]);
  };

  const addFilter = () => {
    if (filterKey && filterVal) {
      setFilters(prev => ({ ...prev, [filterKey]: filterVal }));
      setFilterKey(''); setFilterVal('');
    }
  };

  const removeFilter = (key) => setFilters(prev => { const n = { ...prev }; delete n[key]; return n; });

  const run = async () => {
    if (!selectedSource) { toast.error('Select a data source'); return; }
    if (selectedFields.length === 0) { toast.error('Select at least one field'); return; }
    setLoading(true);
    try {
      const res = await api.post('/reports/generate', {
        source: selectedSource, fields: selectedFields, date_from: dateFrom, date_to: dateTo,
        filters, group_by: groupBy, sort_by: sortBy, sort_order: sortOrder,
      });
      setReportData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate report'); }
    finally { setLoading(false); }
  };

  const saveReport = async () => {
    if (!reportName.trim()) { toast.error('Enter a report name'); return; }
    try {
      await api.post('/reports/save', {
        name: reportName, source: selectedSource, fields: selectedFields,
        filters, date_from: dateFrom, date_to: dateTo, group_by: groupBy,
        sort_by: sortBy, sort_order: sortOrder,
      });
      toast.success('Report saved');
      api.get('/reports/saved').then(r => setSavedReports(r.data?.reports || []));
    } catch { toast.error('Failed to save'); }
  };

  const loadSavedReport = (rpt) => {
    setSelectedSource(rpt.source); setSelectedFields(rpt.fields); setFilters(rpt.filters || {});
    setDateFrom(rpt.date_from || ''); setDateTo(rpt.date_to || ''); setGroupBy(rpt.group_by || '');
    setSortBy(rpt.sort_by || ''); setSortOrder(rpt.sort_order || 'desc'); setReportName(rpt.name);
    toast.info(`Loaded: ${rpt.name}`);
  };

  const deleteSavedReport = async (id) => {
    try { await api.delete(`/reports/saved/${id}`); toast.success('Deleted'); setSavedReports(prev => prev.filter(r => r.report_id !== id)); }
    catch { toast.error('Failed'); }
  };

  const exportCSV = () => {
    if (!reportData?.data?.length) return;
    const cols = reportData.grouped_by ? Object.keys(reportData.data[0]) : selectedFields;
    const header = cols.join(',');
    const rows = reportData.data.map(r => cols.map(c => {
      const v = r[c]; return typeof v === 'string' && v.includes(',') ? `"${v}"` : v ?? '';
    }).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a'); link.href = URL.createObjectURL(blob);
    link.download = `report_${selectedSource}.csv`; link.click();
  };

  const isNumeric = (f) => ['grand_total', 'net_payable', 'total_amount', 'amount', 'adjusted_amount', 'balance_amount', 'tds_amount', 'gst_total', 'total_debit', 'total_credit', 'outstanding', 'count'].some(n => f.includes(n));

  return (
    <div data-testid="report-builder-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-semibold text-2xl tracking-tight text-[hsl(var(--foreground))]">Report Builder</h1>
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[hsl(0,0%,55%)] mt-1">Build custom reports from any data source</p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Left Config Panel */}
        <div className="col-span-4 space-y-4">
          {/* Data Source */}
          <div className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md p-4">
            <h3 className="text-[10px] font-bold text-[hsl(0,0%,55%)] uppercase tracking-[0.12em] mb-2 flex items-center gap-2"><Table2 className="w-3.5 h-3.5" /> Data Source</h3>
            <select value={selectedSource} onChange={e => setSelectedSource(e.target.value)}
              className="form-input" data-testid="report-source-select">
              <option value="">-- Select Source --</option>
              {Object.keys(sources).map(k => <option key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1).replace('_', ' ')}</option>)}
            </select>
          </div>

          {/* Fields */}
          {sourceFields.length > 0 && (
            <div className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md p-4">
              <h3 className="text-[10px] font-bold text-[hsl(0,0%,55%)] uppercase tracking-[0.12em] mb-2 flex items-center gap-2"><Columns className="w-3.5 h-3.5" /> Fields</h3>
              <div className="flex flex-wrap gap-1.5">
                {sourceFields.map(f => (
                  <button key={f} onClick={() => toggleField(f)}
                    className={`px-2 py-1 text-[10px] rounded-md border transition-all duration-200 font-medium ${selectedFields.includes(f) ? 'bg-[hsl(0,0%,3.9%)] text-white border-[hsl(0,0%,3.9%)] dark:bg-white dark:text-[hsl(0,0%,3.9%)] dark:border-white' : 'bg-white dark:bg-[hsl(0,0%,10%)] text-[hsl(0,0%,50%)] border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,20%)] hover:border-[hsl(0,0%,70%)]'}`}
                    data-testid={`field-toggle-${f}`}>
                    {f.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Dates & Sort */}
          <div className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md p-4 space-y-3">
            <h3 className="text-[10px] font-bold text-[hsl(0,0%,55%)] uppercase tracking-[0.12em] mb-1">Date Range & Sort</h3>
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="form-input text-xs" data-testid="report-date-from" />
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="form-input text-xs" data-testid="report-date-to" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select value={groupBy} onChange={e => setGroupBy(e.target.value)} className="form-input text-xs" data-testid="report-group-by">
                <option value="">No grouping</option>
                {sourceFields.map(f => <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>)}
              </select>
              <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} className="form-input text-xs" data-testid="report-sort-order">
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md p-4 space-y-2">
            <h3 className="text-[10px] font-bold text-[hsl(0,0%,55%)] uppercase tracking-[0.12em] mb-1 flex items-center gap-2"><Filter className="w-3.5 h-3.5" /> Filters</h3>
            <div className="flex gap-2">
              <select value={filterKey} onChange={e => setFilterKey(e.target.value)} className="form-input text-xs flex-1">
                <option value="">Field</option>
                {sourceFields.map(f => <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>)}
              </select>
              <input value={filterVal} onChange={e => setFilterVal(e.target.value)} placeholder="Value" className="form-input text-xs flex-1" />
              <Button size="sm" onClick={addFilter} className="btn-primary text-xs" data-testid="add-filter-btn">Add</Button>
            </div>
            {Object.entries(filters).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-xs bg-[hsl(0,0%,96%)] dark:bg-[hsl(0,0%,10%)] rounded-md px-2.5 py-1.5">
                <span className="font-semibold text-[hsl(var(--foreground))]">{k}</span> <span className="text-[hsl(0,0%,55%)]">=</span> <span className="text-[hsl(0,0%,45%)]">{v}</span>
                <button onClick={() => removeFilter(k)} className="ml-auto text-[hsl(var(--destructive))] hover:opacity-70"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>

          {/* Run + Save */}
          <div className="flex gap-2">
            <Button onClick={run} disabled={loading} className="btn-accent flex-1 flex items-center justify-center gap-2" data-testid="run-report-btn">
              <Play className="w-4 h-4" /> {loading ? 'Running...' : 'Run Report'}
            </Button>
          </div>
          <div className="flex gap-2">
            <input value={reportName} onChange={e => setReportName(e.target.value)} placeholder="Report name" className="form-input text-xs flex-1" data-testid="report-name-input" />
            <Button variant="outline" onClick={saveReport} className="btn-secondary flex items-center gap-1.5 text-xs" data-testid="save-report-btn"><Save className="w-3.5 h-3.5" /> Save</Button>
          </div>

          {/* Saved Reports */}
          {savedReports.length > 0 && (
            <div className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md p-4">
              <h3 className="text-[10px] font-bold text-[hsl(0,0%,55%)] uppercase tracking-[0.12em] mb-2">Saved Reports</h3>
              <div className="space-y-1">
                {savedReports.map(r => (
                  <div key={r.report_id} className="flex items-center justify-between bg-[hsl(0,0%,96%)] dark:bg-[hsl(0,0%,10%)] rounded-md px-3 py-2 group">
                    <button onClick={() => loadSavedReport(r)} className="text-[11px] font-medium text-[hsl(var(--foreground))] hover:text-[hsl(221,100%,50%)] text-left flex-1 transition-colors" data-testid={`load-report-${r.report_id}`}>{r.name}</button>
                    <button onClick={() => deleteSavedReport(r.report_id)} className="opacity-0 group-hover:opacity-100 text-[hsl(var(--destructive))] hover:opacity-70 transition" data-testid={`delete-report-${r.report_id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Results Panel */}
        <div className="col-span-8">
          {reportData ? (
            <div className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md overflow-hidden">
              <div className="px-5 py-3 border-b border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold text-sm tracking-tight text-[hsl(var(--foreground))]">
                    Results &mdash; {reportData.total} {reportData.grouped_by ? 'groups' : 'rows'}
                  </h3>
                  <p className="text-[9px] text-[hsl(0,0%,55%)] uppercase tracking-[0.1em]">Source: {reportData.source}</p>
                </div>
                <Button variant="outline" size="sm" onClick={exportCSV} className="btn-secondary flex items-center gap-1.5 text-xs" data-testid="export-csv-btn">
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </Button>
              </div>

              {/* Totals Bar */}
              {reportData.totals && Object.keys(reportData.totals).length > 0 && (
                <div className="px-5 py-2.5 border-b border-[hsl(0,0%,95%)] dark:border-[hsl(0,0%,12%)] flex flex-wrap gap-5">
                  {Object.entries(reportData.totals).map(([k, v]) => (
                    <div key={k}>
                      <span className="text-[9px] font-bold text-[hsl(0,0%,55%)] uppercase tracking-[0.1em]">{k.replace(/_/g, ' ')}</span>
                      <span className="ml-2 text-sm font-bold font-mono text-[hsl(var(--foreground))]">Rs.{formatCurrency(v)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="w-full" data-testid="report-results-table">
                  <thead className="border-b border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] sticky top-0 bg-[hsl(0,0%,98%)] dark:bg-[hsl(0,0%,5%)]">
                    <tr>
                      {reportData.grouped_by ? (
                        Object.keys(reportData.data[0] || {}).map(k => (
                          <th key={k} className={`px-3 py-2.5 text-[10px] font-bold text-[hsl(0,0%,55%)] uppercase tracking-[0.1em] ${isNumeric(k) ? 'text-right' : 'text-left'}`}>
                            {k.replace(/_/g, ' ')}
                          </th>
                        ))
                      ) : selectedFields.map(f => (
                        <th key={f} className={`px-3 py-2.5 text-[10px] font-bold text-[hsl(0,0%,55%)] uppercase tracking-[0.1em] ${isNumeric(f) ? 'text-right' : 'text-left'}`}>
                          {f.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(0,0%,95%)] dark:divide-[hsl(0,0%,12%)]">
                    {reportData.data.map((row, idx) => (
                      <tr key={idx} className="hover:bg-[hsl(0,0%,98%)] dark:hover:bg-[hsl(0,0%,10%)] transition-colors">
                        {(reportData.grouped_by ? Object.entries(row) : selectedFields.map(f => [f, row[f]])).map(([k, v], ci) => (
                          <td key={ci} className={`px-3 py-2 text-sm ${isNumeric(k) ? 'text-right font-mono' : ''}`}>
                            {v === null || v === undefined ? <span className="text-[hsl(0,0%,75%)]">-</span>
                              : typeof v === 'number' ? (isNumeric(k) ? `Rs.${formatCurrency(v)}` : v)
                              : String(v)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md flex flex-col items-center justify-center py-32">
              <BarChart3 className="w-14 h-14 text-[hsl(0,0%,85%)] dark:text-[hsl(0,0%,20%)] mb-4" strokeWidth={1} />
              <p className="text-[hsl(0,0%,55%)] text-sm">Select a data source and run your report</p>
              <p className="text-[hsl(0,0%,70%)] text-[11px] mt-1">Choose fields, filters, and grouping from the left panel</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
